import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import {
    ConfigurableChartDashboard,
    DASHBOARD_CHART_VIEW_CONFIG,
    DashboardChartView,
} from '../types';

type DashboardChartViews = Partial<Record<ConfigurableChartDashboard, DashboardChartView>>;

interface DashboardChartViewContextValue {
    chartViews: DashboardChartViews;
    setChartView: (dashboard: ConfigurableChartDashboard, view: DashboardChartView) => void;
}

const DashboardChartViewContext = createContext<DashboardChartViewContextValue | undefined>(undefined);

export const DashboardChartViewProvider = ({ children }: { children: ReactNode }) => {
    const [chartViews, setChartViews] = useState<DashboardChartViews>({});
    const setChartView = useCallback((dashboard: ConfigurableChartDashboard, view: DashboardChartView) => {
        setChartViews((current) => ({ ...current, [dashboard]: view }));
    }, []);
    const value = useMemo(() => ({ chartViews, setChartView }), [chartViews, setChartView]);

    return <DashboardChartViewContext.Provider value={value}>{children}</DashboardChartViewContext.Provider>;
};

export const useDashboardChartView = (dashboard: ConfigurableChartDashboard) => {
    const context = useContext(DashboardChartViewContext);
    if (!context) throw new Error('useDashboardChartView must be used within DashboardChartViewProvider.');

    const chartView = context.chartViews[dashboard] ?? DASHBOARD_CHART_VIEW_CONFIG[dashboard].recommendedView;
    const setChartView = useCallback(
        (view: DashboardChartView) => context.setChartView(dashboard, view),
        [context, dashboard]
    );

    return [chartView, setChartView] as const;
};
